import { stringSetQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { usePercentSerialiser } from "hooks/serialisers/percent";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import type { LegendClickEvent, PlotMouseEvent } from "plotly.js";
import type { PerformanceTimeseriesBucket } from "src/store/api/generatedApi";
import { useResolvedScrapViewportTimeZone } from "store/slices/scrapTrackingViewportSlice";
import { formatHoverTimeRange } from "../components/timelineChartUtils";
import theme from "src/theme";
type PerformanceDisplayMode = "rate" | "count";

type ModelPerformanceTimelineProps = Readonly<{
  buckets: readonly PerformanceTimeseriesBucket[];
  start: Date;
  end: Date;
  displayMode: PerformanceDisplayMode;
  onBarClick?: (bucketStart: Date) => void;
}>;

const FN_COLOR = theme.palette.error.main;
const FP_COLOR = theme.palette.info.main;

interface AggregatedBucket {
  bucketStart: string;
  bucketEnd: Date;
  barWidth: number;
  midpoint: Date;
  timeRange: string;
  totalVerified: number;
  totalLabeller: number;
  totalMatched: number;
}

const aggregateBucketsByTime = (
  buckets: readonly PerformanceTimeseriesBucket[],
  timeZone: string
): AggregatedBucket[] => {
  const grouped = new Map<string, PerformanceTimeseriesBucket[]>();
  for (const bucket of buckets) {
    const key = bucket.bucket_start;
    const existing = grouped.get(key);
    if (existing) {
      existing.push(bucket);
    } else {
      grouped.set(key, [bucket]);
    }
  }

  return [...grouped.entries()].flatMap(([, group]) => {
    const first = group[0];
    if (!first) return [];
    const bucketStart = new Date(first.bucket_start);
    const bucketEnd = new Date(first.bucket_end);
    const durationMs = bucketEnd.getTime() - bucketStart.getTime();

    let totalVerified = 0;
    let totalLabeller = 0;
    let totalMatched = 0;

    for (const b of group) {
      totalVerified += b.total_verified_events;
      totalLabeller += b.total_labeller_events;
      totalMatched += b.matched_events;
    }

    return {
      bucketStart: first.bucket_start,
      bucketEnd,
      barWidth: durationMs * 0.4,
      midpoint: new Date((bucketStart.getTime() + bucketEnd.getTime()) / 2),
      timeRange: formatHoverTimeRange(
        first.bucket_start,
        first.bucket_end,
        timeZone
      ),
      totalVerified,
      totalLabeller,
      totalMatched,
    };
  });
};

const computeYAxisMax = (
  traces: readonly { y?: readonly number[] }[],
  displayMode: PerformanceDisplayMode
): number => {
  const maxY = Math.max(0, ...traces.flatMap((trace) => trace.y ?? []));

  if (displayMode === "rate") {
    const rounded = Math.ceil(maxY * 10) / 10;
    return Math.max(rounded, 0.1);
  }

  const rounded = Math.ceil(maxY / 10) * 10;
  return Math.max(rounded, 10);
};

const buildCountTraces = (
  aggregated: AggregatedBucket[],
  fnLabel: string,
  fpLabel: string
) => {
  const fnX: Date[] = [];
  const fnY: number[] = [];
  const fnHover: string[] = [];
  const fnWidths: number[] = [];
  const fnBucketStarts: string[] = [];

  const fpX: Date[] = [];
  const fpY: number[] = [];
  const fpHover: string[] = [];
  const fpWidths: number[] = [];
  const fpBucketStarts: string[] = [];

  for (const bucket of aggregated) {
    const totalFn = bucket.totalVerified - bucket.totalMatched;
    const totalFp = bucket.totalLabeller - bucket.totalMatched;

    if (totalFn > 0) {
      fnX.push(bucket.midpoint);
      fnBucketStarts.push(bucket.bucketStart);
      fnY.push(totalFn);
      fnWidths.push(bucket.barWidth);
      fnHover.push(`${bucket.timeRange}<br>FN: ${totalFn}`);
    }

    if (totalFp > 0) {
      fpX.push(bucket.midpoint);
      fpBucketStarts.push(bucket.bucketStart);
      fpY.push(totalFp);
      fpWidths.push(bucket.barWidth);
      fpHover.push(`${bucket.timeRange}<br>FP: ${totalFp}`);
    }
  }

  return [
    {
      x: fnX,
      customdata: fnBucketStarts,
      y: fnY,
      type: "bar" as const,
      name: fnLabel,
      marker: { color: FN_COLOR },
      width: fnWidths,
      hovertext: fnHover,
      hoverinfo: "text" as const,
    },
    {
      x: fpX,
      customdata: fpBucketStarts,
      y: fpY,
      type: "bar" as const,
      name: fpLabel,
      marker: { color: FP_COLOR },
      width: fpWidths,
      hovertext: fpHover,
      hoverinfo: "text" as const,
    },
  ];
};

const buildRateTraces = (
  aggregated: AggregatedBucket[],
  formatPercent: (value?: number) => string,
  jaccardLabel: string,
  fnLabel: string,
  fpLabel: string
) => {
  const fnX: Date[] = [];
  const fnY: number[] = [];
  const fnHover: string[] = [];
  const fnWidths: number[] = [];
  const fnBucketStarts: string[] = [];

  const fpX: Date[] = [];
  const fpY: number[] = [];
  const fpHover: string[] = [];
  const fpWidths: number[] = [];
  const fpBucketStarts: string[] = [];

  for (const bucket of aggregated) {
    const totalFn = bucket.totalVerified - bucket.totalMatched;
    const totalFp = bucket.totalLabeller - bucket.totalMatched;
    const fnRate =
      bucket.totalVerified > 0 ? totalFn / bucket.totalVerified : null;
    const fpRate =
      bucket.totalLabeller > 0 ? totalFp / bucket.totalLabeller : null;
    const denominator = bucket.totalMatched + totalFn + totalFp;
    const jaccard = denominator > 0 ? bucket.totalMatched / denominator : null;

    const jaccardSuffix =
      jaccard !== null
        ? `<br>${jaccardLabel}: ${formatPercent(jaccard * 100)}`
        : "";

    if (fnRate !== null) {
      fnX.push(bucket.midpoint);
      fnBucketStarts.push(bucket.bucketStart);
      fnY.push(fnRate);
      fnWidths.push(bucket.barWidth);
      fnHover.push(
        `${bucket.timeRange}<br>FN: ${totalFn} / ${bucket.totalVerified} verified (${formatPercent(fnRate * 100)})${jaccardSuffix}`
      );
    }

    if (fpRate !== null) {
      fpX.push(bucket.midpoint);
      fpBucketStarts.push(bucket.bucketStart);
      fpY.push(fpRate);
      fpWidths.push(bucket.barWidth);
      fpHover.push(
        `${bucket.timeRange}<br>FP: ${totalFp} / ${bucket.totalLabeller} predicted (${formatPercent(fpRate * 100)})${jaccardSuffix}`
      );
    }
  }

  return [
    {
      x: fnX,
      customdata: fnBucketStarts,
      y: fnY,
      type: "bar" as const,
      name: fnLabel,
      marker: { color: FN_COLOR },
      width: fnWidths,
      hovertext: fnHover,
      hoverinfo: "text" as const,
      offsetgroup: "fn",
    },
    {
      x: fpX,
      customdata: fpBucketStarts,
      y: fpY,
      type: "bar" as const,
      name: fpLabel,
      marker: { color: FP_COLOR },
      width: fpWidths,
      hovertext: fpHover,
      hoverinfo: "text" as const,
      offsetgroup: "fp",
    },
  ];
};

const chartLayout = (
  start: Date,
  end: Date,
  displayMode: PerformanceDisplayMode,
  yAxisMax: number
) => ({
  height: 300,
  margin: { l: 50, r: 50, t: 30, b: 30 },
  showlegend: true,
  legend: { orientation: "h" as const, y: 1.15 },
  xaxis: {
    type: "date" as const,
    range: [start, end],
    showgrid: true,
    gridcolor: theme.palette.grey[100],
  },
  yaxis: {
    visible: true,
    fixedrange: true,
    range: [0, yAxisMax],
    ...(displayMode === "rate" ? { tickformat: ".0%" } : {}),
  },
  barmode: "group" as const,
  hovermode: "x unified" as const,
  dragmode: "pan" as const,
});

const chartConfig = {
  responsive: true,
  scrollZoom: false,
};

export const ModelPerformanceTimeline = ({
  buckets,
  start,
  end,
  displayMode,
  onBarClick,
}: ModelPerformanceTimelineProps) => {
  const { t } = useTenantTranslation();
  const { format: formatPercent } = usePercentSerialiser({
    decimalPlaces: 1,
    includeSymbol: true,
  });
  const jaccardLabel = t("jaccardAccuracy");
  const fnLabel = t("falseNegativeRate");
  const fpLabel = t("falsePositiveRate");

  const timeZone = useResolvedScrapViewportTimeZone();
  const aggregated = useMemo(
    () => aggregateBucketsByTime(buckets, timeZone),
    [buckets, timeZone]
  );
  const traces = useMemo(
    () =>
      displayMode === "count"
        ? buildCountTraces(aggregated, fnLabel, fpLabel)
        : buildRateTraces(
            aggregated,
            formatPercent,
            jaccardLabel,
            fnLabel,
            fpLabel
          ),
    [aggregated, displayMode, formatPercent, jaccardLabel, fnLabel, fpLabel]
  );

  const [hiddenTraces, setHiddenTraces] = useNavigationQuery(
    "performanceHiddenTraces",
    new Set<string>(),
    stringSetQuery,
    "replace"
  );
  const visibleTraces = useMemo(
    () =>
      traces.map((trace) => ({
        ...trace,
        visible: hiddenTraces.has(trace.name)
          ? ("legendonly" as const)
          : (true as const),
      })),
    [traces, hiddenTraces]
  );
  const yAxisMax = useMemo(
    () =>
      computeYAxisMax(
        visibleTraces.filter((trace) => trace.visible === true),
        displayMode
      ),
    [visibleTraces, displayMode]
  );
  const layout = useMemo(
    () => chartLayout(start, end, displayMode, yAxisMax),
    [start, end, displayMode, yAxisMax]
  );

  const handleLegendClick = useCallback(
    (event: Readonly<LegendClickEvent>) => {
      const traceName = event.data[event.curveNumber]?.name;
      if (typeof traceName !== "string") return false;
      setHiddenTraces((prev) => {
        const next = new Set(prev);
        if (next.has(traceName)) {
          next.delete(traceName);
        } else {
          next.add(traceName);
        }
        return next;
      });
      return false;
    },
    [setHiddenTraces]
  );

  const handlePlotClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => {
      if (!onBarClick) return;
      const point = event.points[0];
      if (!point) return;
      if (typeof point.customdata !== "string") return;
      const bucketStart = new Date(point.customdata);
      if (isNaN(bucketStart.getTime())) return;
      onBarClick(bucketStart);
    },
    [onBarClick]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <ScrapTrackingViewportPlot
        data={visibleTraces}
        layout={layout}
        config={chartConfig}
        onClick={onBarClick ? handlePlotClick : undefined}
        onLegendClick={handleLegendClick}
        style={{ width: "100%", cursor: onBarClick ? "pointer" : undefined }}
      />
    </Box>
  );
};
